import React, { useState, useEffect, useRef, useContext } from "react";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import "./Map.css";
import MapContext from "./MapContext";
import { inAndOut, easeIn } from "ol/easing";
import { useDispatch, useSelector } from "react-redux";
import { setActiveTool } from "../redux/toolSelectSlice";
import { IoHandRightOutline } from "react-icons/io5";
import {
    TbBrandFlightradar24,
    TbCurrentLocation,
    TbCurrentLocationOff,
} from "react-icons/tb";
import { GetCurrentGPSLocation } from "./GPS/gpsUtil";
import { getMapInteractionById } from "./utils/mapUtils";

const mobile = window.innerWidth < 601 ? true : false;

const MapControls = () => {
    const { map } = useContext(MapContext);
    // const [activeTool, setActiveTool] = useState("pan");
    const activeTool = useSelector((state) => state.toolSelect.tool);
    const dispatch = useDispatch();
    const [currentPanAction, setCurrentPanAction] = useState("");

    const nextExtents = useRef([]);
    const previousExtents = useRef([]);
    const currentExtent = useRef();

    const nextButtonFired = useRef();
    const previousButtonFired = useRef();

    const [gpsButtonPermisson, setGPSButtonPermisson] = useState("prompt");
    const [gpsModeState, setGPSModeState] = useState("off");
    const gpsMode = useRef("off");

    const [previousButtonDisabled, setPreviousButtonDisabled] = useState(true);
    const [nextButtonDisabled, setNextButtonDisabled] = useState(true);

    const activeToolRef = useRef();
    activeToolRef.current = activeTool;

    // Map move event fires when map loads and anytime it moves
    const handleMoveEnd = (evt) => {
        let newExtent = map.getView().calculateExtent(map.getSize());
        // console.log('%ccurrent','color:red;',newExtent)
        // On initial load
        if (!currentExtent.current) {
            previousButtonFired.current = false;
            nextButtonFired.current = false;
        }
        // fires when back/next buttons are not hit
        else if (
            (previousButtonFired.current === false) &
            (nextButtonFired.current === false)
        ) {
            previousExtents.current = [currentExtent.current, ...previousExtents.current];
            if (previousButtonDisabled === true) {
                setPreviousButtonDisabled(false);
            }
            // reset next extents
            setNextButtonDisabled(true);
            nextExtents.current = [];
        } else {
            // do nothing
        }

        currentExtent.current = newExtent;

        nextButtonFired.current = false;
        previousButtonFired.current = false;
    };

    // Need to revisit to optimize
    // Methods push/pop run fast, while shift/unshift are slow.
    // https://javascript.info/array >performace
    const handlePreviousExtent = () => {
        previousButtonFired.current = true;
        map.getView().fit(previousExtents.current[0], {
            duration: 750,
            easing: inAndOut,
        });

        // Drop current extent from previousExtent array
        let updatedArray = previousExtents.current;
        updatedArray.shift();
        previousExtents.current = [...updatedArray];

        // Hand current extent to next button
        nextExtents.current = [currentExtent.current, ...nextExtents.current];

        // Check to see if buttons need updating
        if (previousExtents.current.length === 0) {
            setPreviousButtonDisabled(true);
        }
        if (nextButtonDisabled === true) setNextButtonDisabled(false);
    };

    const handleNextExtent = () => {
        previousButtonFired.current = true;
        map.getView().fit(nextExtents.current[0], { duration: 750, easing: inAndOut });

        // Drop current extent from nextExtent array
        let updatedArray = nextExtents.current;
        updatedArray.shift();
        nextExtents.current = [...updatedArray];

        // Hand current extent to previous button
        previousExtents.current = [currentExtent.current, ...previousExtents.current];

        // Check to see if buttons needs updating
        if (previousButtonDisabled === true) {
            setPreviousButtonDisabled(false);
        }
        if (nextExtents.current.length === 0) {
            setNextButtonDisabled(true);
        }
    };
    const mapConfig = useSelector((state) => state.configData.mapConfig);
    // Zoom to full extent

    const numExtent = mapConfig.map.fullExtent.split(",").map((e) => Number(e));

    const handleFullExtent = () => {
        map.getView().fit(numExtent, {
            duration: 750,
            easing: inAndOut,
        });
    };

    const handleTogglableClick = (buttonPressed) => {
        dispatch(setActiveTool(buttonPressed));
    };

    const handleBoxEnd = (dragBox) => {
        const extent = dragBox.getGeometry().getExtent();
        if (activeToolRef.current === "zoom-in") {
            map.getView().fit(extent, { duration: 750, easing: inAndOut });
        } else if (activeToolRef.current === "zoom-out") {
            const currentZoom = map.getView().getZoom();
            map.getView().animate({ zoom: currentZoom - 1 });
        }
    };

    const handlePointer = () => {
        if (activeToolRef.current === "zoom-in" || activeToolRef.current === "zoom-out") {
            map.getViewport().style.cursor = "crosshair";
        } else {
            map.getViewport().style.cursor = "";
        }
    };

    // Turns off tooltips if mobile === true
    const hideIfMobile = () => {
        return !mobile;
    };

    useEffect(() => {
        //console.log(activeTool);
        if (!map) return;
        if (activeTool === "zoom-in" || activeTool === "zoom-out") {
            const interaction = getMapInteractionById(map, "drag-box-zoom");
            interaction.setActive(true);
        } else {
            const interaction = getMapInteractionById(map, "drag-box-zoom");
            interaction.setActive(false);
        }
    }, [activeTool]);

    useEffect(() => {
        if (!map) return;
        map.on("pointermove", () => handlePointer());
        // the handleMoveEnd function must be called as an anonymous function (as in below)
        // or as a reference to prevent it from being run with useEffect
        map.on("moveend", handleMoveEnd);
        const dragBox = getMapInteractionById(map, "drag-box-zoom");
        dragBox.on("boxend", () => handleBoxEnd(dragBox));
    }, [map]);

    // something in this function is causing Firefox version 101.1 not to load
    function handlePermission() {
        /* navigator.permissions.query({ name: "geolocation" }).then((result) => {
       report(result.state);
       if (result.state === "granted") {
       } else if (result.state === "prompt") {
       }
       result.addEventListener("change", () => {
         report(result.state);
       });
     });*/
    }

    handlePermission();
    function report(state) {
        setGPSButtonPermisson(state);
    }

    function gps() {
        GetCurrentGPSLocation(map, gpsMode.current);
        setTimeout(() => {
            if (gpsMode.current != "off") {
                gps();
            }
        }, 1000);
    }

    function toogleMode() {
        //console.log(gpsMode.current)
        switch (gpsMode.current) {
            case "off":
                gpsMode.current = "on";
                setGPSModeState("on");
                gps();
                break;
            case "on":
                gpsMode.current = "track";
                setGPSModeState("track");
                break;
            case "track":
                gpsMode.current = "off";
                setGPSModeState("off");
                GetCurrentGPSLocation(map, gpsMode.current);
                break;
            default:
                gpsMode.current = "off";
                setGPSModeState("off");
                GetCurrentGPSLocation(map, gpsMode.current);
                break;
        }
    }

    return (
        <div className="map-buttons">
            <Tooltip filter={hideIfMobile} position="right" parentTitle={true}>
                <ButtonGroup>
                    <Button
                        title="Zoom-in"
                        icon="zoom-in"
                        onClick={() => handleTogglableClick("zoom-in")}
                        selected={activeTool === "zoom-in"}
                        togglable
                    />

                    <Button
                        title="Zoom out"
                        icon="zoom-out"
                        onClick={() => handleTogglableClick("zoom-out")}
                        selected={activeTool === "zoom-out"}
                        togglable
                    />

                    <Button
                        title="Pan control"
                        //icon="pan"
                        onClick={() => handleTogglableClick("pan")}
                        selected={activeTool === "pan"}
                        togglable>
                        <IoHandRightOutline />
                    </Button>

                    <Button
                        title="Pan to full extent"
                        icon="fullscreen"
                        onClick={handleFullExtent}
                    />

                    <Button
                        id={"previous-extent"}
                        title="Previous extent"
                        icon="arrow-left"
                        onClick={handlePreviousExtent}
                        disabled={previousButtonDisabled}
                        selected={currentPanAction === "previous"}
                    />

                    <Button
                        id={"next-extent"}
                        title="Next extent"
                        icon="arrow-right"
                        onClick={handleNextExtent}
                        disabled={nextButtonDisabled}
                        selected={currentPanAction === "next"}
                    />
                    {(gpsButtonPermisson === "prompt" ||
                        gpsButtonPermisson === "granted") &&
                        gpsModeState === "off" && (
                            <>
                                <Button
                                    id={"gps-button-off"}
                                    title="GPS Location"
                                    onClick={toogleMode}>
                                    <TbCurrentLocationOff />
                                </Button>
                            </>
                        )}
                    {(gpsButtonPermisson === "prompt" ||
                        gpsButtonPermisson === "granted") &&
                        gpsModeState === "on" && (
                            <>
                                <Button
                                    id={"gps-button-on"}
                                    title="GPS Location"
                                    onClick={toogleMode}>
                                    <TbCurrentLocation />
                                </Button>
                            </>
                        )}
                    {(gpsButtonPermisson === "prompt" ||
                        gpsButtonPermisson === "granted") &&
                        gpsModeState === "track" && (
                            <>
                                <Button
                                    id={"gps-button-track"}
                                    title="GPS Track"
                                    onClick={toogleMode}>
                                    <TbBrandFlightradar24 />
                                </Button>
                            </>
                        )}
                </ButtonGroup>
            </Tooltip>
        </div>
    );
};

export default MapControls;
